import { format, formatDistance, sub } from "date-fns"
import { enUS } from "date-fns/locale"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import type { Locale } from "date-fns"

import { languages } from "../i18n/languages"

const localeCache = new Map<string, Locale>()

// TODO: We rely on the current language to determine the date locale. This is not ideal, as we use en-US for the english translation.
// We either need to also have an en-GB translation or we need to separate the date locale from the translation language.
export const useDate = () => {
  const { i18n } = useTranslation()
  const [locale, setLocale] = useState<Locale>(() => localeCache.get(i18n.language) || enUS)

  useEffect(() => {
    const langCode = i18n.language
    if (localeCache.has(langCode)) {
      setLocale(localeCache.get(langCode)!)
      return
    }

    const lang = languages.find((l) => l.code === langCode)
    if (lang && typeof lang.date_locale === "function") {
      lang.date_locale()
        .then((loadedLocale: Locale) => {
          localeCache.set(langCode, loadedLocale)
          setLocale(loadedLocale)
        })
        .catch(() => {
          setLocale(enUS)
        })
    } else {
      setLocale(enUS)
    }
  }, [i18n.language])

  const getFullDate = ({
    date,
    includeTime = false,
  }: {
    date: string | Date
    includeTime?: boolean
  }) => {
    const ensuredDate = new Date(date)

    if (isNaN(ensuredDate.getTime())) {
      return ""
    }

    const timeFormat = includeTime ? "p" : ""

    return format(ensuredDate, `PP ${timeFormat}`, {
      locale,
    })
  }

  function getRelativeDate(date: string | Date): string {
    const now = new Date()

    return formatDistance(sub(new Date(date), { minutes: 0 }), now, {
      addSuffix: true,
      locale,
    })
  }

  return {
    getFullDate,
    getRelativeDate,
  }
}
